import React, {useState, useEffect} from 'react';
import productService from '../../../services/productService';
import {message as antdMessage, Modal, Spin, Button} from 'antd';
import {GiftOutlined} from '@ant-design/icons';
import {hideSubscriptionModal} from '../../../components/molecules/Modals/subscription/component';

import {loadStripe} from '@stripe/stripe-js';
import {Elements, PaymentElement, useStripe, useElements} from '@stripe/react-stripe-js';
import {formatValue, useIsMobile} from '../../../utils/helpers';
import {refreshCurrentUser} from "../../../store/modules/auth/refreshUser";

let globalProduct: any = null;
export const setProduct = (newProduct: any) => {
    globalProduct = newProduct;
};
export const getProduct = () => globalProduct;

// CheckoutForm now handles success/failure inline
const CheckoutForm: React.FC<{
    onSuccess: (sessionId: string) => void;
    onError: (msg: string) => void;
    intentType: 'payment' | 'setup';
}> = ({onSuccess, onError, intentType}) => {
    const stripe = useStripe();
    const elements = useElements();

    const [elementComplete, setElementComplete] = useState(false);
    const [elementError, setElementError] = useState<string>('');
    const [submitting, setSubmitting] = useState(false);

    const canSubmit = !!stripe && !!elements && elementComplete && !submitting && !elementError;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!canSubmit) return;

        setSubmitting(true);

        try {
            if (intentType === 'payment') {
                const {error, paymentIntent} = await stripe.confirmPayment({elements, redirect: 'if_required'});

                if (error) {
                    onError(error.message || 'Setup confirmation failed.');
                } else if (paymentIntent?.status === 'succeeded') {
                    onSuccess(paymentIntent.id);
                } else {
                    onError('Setup status: ' + (paymentIntent?.status || 'unknown'));
                }
            } else {
                const {error, setupIntent} = await stripe.confirmSetup({elements, redirect: 'if_required'});

                if (error) {
                    onError(error.message || '');
                } else if (setupIntent?.status === 'succeeded') {
                    onSuccess(setupIntent.id);
                } else {
                    const msg = 'Setup status: ' + setupIntent?.status;
                    onError(setupIntent?.status || '');
                }
            }
        } catch (err: any) {
            onError(err.message || 'Unexpected error');
        }

        setSubmitting(false);
    };

    return (
        <Spin spinning={submitting} tip="Processing payment…">
            <form onSubmit={handleSubmit} style={{display: 'flex', flexDirection: 'column'}}>
                <PaymentElement
                    onChange={(ev: any) => {
                        setElementComplete(ev.complete);
                        setElementError(ev.error?.message || '');
                        onError('');
                    }}
                />

                {elementError && (
                    <div style={{color: 'red', margin: '8px 0'}}>
                        {elementError}
                    </div>
                )}

                <div style={{marginTop: 16, display: 'flex', justifyContent: 'flex-end'}}>
                    <button
                        type="submit"
                        disabled={!canSubmit}
                        style={{
                            backgroundColor: '#1677ff',
                            color: '#fff',
                            border: 'none',
                            padding: '6px 12px',
                            fontSize: '13px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            marginTop: '16px'
                        }}
                    >
                        Submit
                    </button>
                </div>
            </form>
        </Spin>
    );
};

// Elements wrapper
const EmbeddedCheckout: React.FC<{
    clientSecret: string;
    intentType: 'payment' | 'setup';
    stripePK: string;
    onSuccess: (sessionId: string) => void;
    onError: (msg: string) => void;
}> = ({clientSecret, intentType, stripePK, onSuccess, onError}) => {
    const options = {clientSecret};
    const stripePromise = loadStripe(stripePK);

    return (
        <Elements stripe={stripePromise} options={options}>
            <CheckoutForm onSuccess={onSuccess} onError={onError} intentType={intentType}/>
        </Elements>
    );
};

// ProductList with conditional grid & spinner
const ProductList: React.FC<{
    onStartCheckout: (product: any, cs: string, type: 'payment' | 'setup') => void;
    isGrid: boolean;
}> = ({onStartCheckout, isGrid}) => {
    const [products, setProducts] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [fetchError, setFetchError] = useState(false);
    const isMobile = useIsMobile();

    useEffect(() => {
        setLoading(true);
        productService.getProducts()
            .then(setProducts)
            .catch(err => {
                if (err.response?.status === 409) {
                    Modal.error({
                        title: 'Subscription Already Exists',
                        content: err.response.data?.message || 'You already have an existing subscription.'
                    });
                    setFetchError(true);
                } else {
                    antdMessage.error(err.message || 'Failed to load products.');
                }
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    const handleSubscribe = async (product: any) => {
        setLoading(true);
        try {
            const paymentIntent = await productService.checkSubscription();

            if (paymentIntent.clientSecret) {
                setProduct(product);
                onStartCheckout(product, paymentIntent.clientSecret, 'setup');
            } else {
                const {
                    clientSecret,
                    intentType
                } = await productService.createSubscription({priceId: product.default_price.id});
                setProduct(product);
                onStartCheckout(product, clientSecret, intentType);
            }
        } catch (err: any) {
            antdMessage.error(err.message || 'Session creation failed.');
            setLoading(false);
        }
    };

    if (fetchError) return null;

    const fmt = (p: any) =>
        p?.unit_amount ? `${formatValue(p.unit_amount / 100)} ${p.currency.toUpperCase()}` : 'N/A';

    return (
        <Spin spinning={loading} tip="Creating your checkout session…">
            <section
                style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : (isGrid ? 'repeat(2, 1fr)' : '1fr'),
                    gap: isMobile ? 12 : 16,
                    padding: isMobile ? 8 : 16,
                }}
            >
                {products.map(prod => (
                    <div
                        key={prod.id}
                        style={{
                            border: '1px solid #ccc',
                            borderRadius: 8,
                            padding: isMobile ? 12 : 16,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            overflow: 'hidden',
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                gap: isMobile ? 10 : 16,
                                flexDirection: isMobile ? 'column' : 'row',
                                alignItems: isMobile ? 'flex-start' : 'center',
                            }}
                        >
                            <div style={{alignSelf: isMobile ? 'center' : 'flex-start'}}>
                                <Logo product={prod}/>
                            </div>
                            <div style={{flex: 1, minWidth: 0}}>
                                <h3 style={{margin: 0, fontSize: isMobile ? 14 : 16, lineHeight: 1.3}}>{prod.name}</h3>
                                {prod.default_price && (
                                    <>
                                        <p style={{margin: '8px 0 0', fontSize: isMobile ? 13 : 14}}>
                                            <strong>Price:</strong> {fmt(prod.default_price)}
                                        </p>
                                        <p style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 6,
                                            color: '#faad14',
                                            fontWeight: 700,
                                            margin: '6px 0 0',
                                            fontSize: isMobile ? 12 : 13,
                                        }}>
                                            <GiftOutlined/>1-month free trial
                                        </p>
                                    </>
                                )}
                                <p style={{margin: '8px 0 0', fontSize: isMobile ? 12 : 13}}><em>{prod.description}</em>
                                </p>
                            </div>
                        </div>
                        <div style={{textAlign: 'right', marginTop: isMobile ? 10 : 12}}>
                            <button
                                onClick={() => handleSubscribe(prod)}
                                style={{
                                    backgroundColor: '#1677ff',
                                    color: '#fff',
                                    border: 'none',
                                    padding: isMobile ? '8px 12px' : '6px 12px',
                                    fontSize: isMobile ? 14 : 13,
                                    borderRadius: 6,
                                    cursor: 'pointer',
                                    width: isMobile ? '100%' : 'auto',
                                }}
                            >
                                Subscribe
                            </button>
                        </div>
                    </div>
                ))}
            </section>
        </Spin>
    );
};

// Top‑level App stitches it together
export default function App() {
    const stripePK = process.env.REACT_APP_STRIPE_PK || '';
    const isMobile = useIsMobile();

    const [flow, setFlow] = useState<{
        stage: 'list' | 'checkout' | 'success' | 'error';
        clientSecret?: string;
        intentType?: 'payment' | 'setup';
        sessionId?: string;
        errorMsg?: string;
        product?: any;
        retrying?: boolean;
    }>({stage: 'list'});

    const startCheckout = (product: any, clientSecret: string, intentType: 'payment' | 'setup') => {
        setFlow({
            stage: 'checkout',
            clientSecret,
            intentType,
            errorMsg: undefined,
            product,
            retrying: false,
        });
    };

    return (
        <>
            {flow.stage === 'list' && (
                <ProductList onStartCheckout={startCheckout} isGrid={!isMobile}/>
            )}

            {flow.stage === 'checkout' && flow.clientSecret && flow.intentType && stripePK && (
                <section style={{padding: '16px'}}>
                    <h2>Complete your payment</h2>

                    <Spin spinning={!!flow.retrying} tip="Preparing retry…">
                        <EmbeddedCheckout
                            clientSecret={flow.clientSecret}
                            intentType={flow.intentType}
                            stripePK={stripePK}
                            onSuccess={(id) => setFlow({...flow, stage: 'success', sessionId: id})}
                            onError={(msg) => setFlow({...flow, errorMsg: msg ? msg : undefined})}
                        />
                    </Spin>

                    {!!flow.errorMsg && (
                        <div style={{marginTop: 12}}>
                            <div style={{color: 'red', marginBottom: 8}}>{flow.errorMsg}</div>
                        </div>
                    )}
                </section>
            )}

            {flow.stage === 'success' && flow.sessionId && (
                <SuccessDisplay product={flow.product}/>
            )}

            {flow.stage === 'error' && (
                <Message message={flow.errorMsg || 'Something went wrong.'}/>
            )}
        </>
    );
}

// Helpers
const Logo: React.FC<{ product: any }> = ({product}) => {
    const url = product.images?.[0] || 'https://via.placeholder.com/150';
    return (
        <img
            src={url}
            alt={product.name}
            style={{
                width: 80,
                height: 80,
                objectFit: 'contain',
                borderRadius: 4,
                flexShrink: 0
            }}
        />
    );
};

interface SuccessDisplayProps {
    product: any;
}

const SuccessDisplay: React.FC<SuccessDisplayProps> = ({product}) => {
    useEffect(() => {
        hideSubscriptionModal();
        refreshCurrentUser();
    }, []);

    const handleManageBilling = async () => {
        try {
            const response = await productService.createPortalSession({});
            if (response?.data?.url) {
                window.location.href = response.data.url;
            } else {
                antdMessage.error('No portal URL received');
            }
        } catch (err: any) {
            antdMessage.error(err.message || 'Error creating portal session.');
        }
    };

    const goHome = () => {
        window.location.href = '/home';
    };

    return (
        <Modal
            title="Subscription Active"
            visible
            width={600}
            onCancel={goHome}
            footer={[
                <Button key="home" type="primary" onClick={goHome}>Home</Button>,
                <Button key="manage" onClick={handleManageBilling}>Manage Billing</Button>,
            ]}
        >
            <div style={{display: 'flex', alignItems: 'center', gap: 16}}>
                <Logo product={product}/>
                <div>
                    <p>
                        Congratulations! Your subscription is now live—you have full access to all of Promotify’s
                        features.
                    </p>
                    <br/>
                    <h3>
                        To view or modify your plan or billing details, click <strong>Manage Billing</strong>.
                    </h3>
                </div>
            </div>
        </Modal>
    );
};

const Message: React.FC<{ message: string }> = ({message}) => (
    <section style={{padding: '16px'}}>
        <p>{message}</p>
    </section>
);